import { FormProvider, useForm } from 'react-hook-form';
import { InputHookForm } from '../../Common/Form/input-with-hook-form';
import DropzoneComponent from '../../Dropzone';
import IntroComponent from '../components/intro.components';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextAreaHookForm } from '../../Common/Form/textarea-hook-form';
import { profileSchema } from './profile.schema';
import Button from '../../Button';
import FormInputCounter from '../../Common/Form/InputCountCharacter/form-input-counter.component';
import PreviewProfile from './PreviewProfile';
import FacebookIcon from '../../../assets/facebook-icon.svg';
import LinkedinIcon from '../../../assets/linkedin-icon.svg';
import InstagramIcon from '../../../assets/instagram-icon.svg';
import TwitterIcon from '../../../assets/twitter-icon.svg';
import {
  ALLOWED_FILE_TYPES_PROFILE,
  getContentTypeAvatar,
  MAX_FILE_SIZE_30
} from '../../../constants/image.constant';
import { useEffect, useState } from 'react';
import { genPresignUrl, updateProfile } from '../../../apis/article.api';
import axios from 'axios';
import { useAtom } from 'jotai';
import { profileAtom } from '../../Settings/atom';
import { useMediaQuery, useToast } from '@chakra-ui/react';
import ToastBtnMessage from '../../ToastBtnMessage';
import DataLayer from '../../../utils/data-layer';

const ProfileComponent = () => {
  const [fileImg, setFileImg] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useAtom(profileAtom);
  const toast = useToast();
  const [isMobile] = useMediaQuery('(max-width: 768px)');

  const methods = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      username: '',
      description: '',
      facebook: '',
      instagram: '',
      linkedin: '',
      twitter: ''
    }
  });

  const validateFiles = async (file) => {
    let messageError = '';
    const validFiles = file[0];

    if (!ALLOWED_FILE_TYPES_PROFILE.includes(validFiles?.type)) {
      messageError = 'Please ensure your file type is SVG, PNG, JPG, JPEG, GIF';
    }

    if (validFiles?.size > MAX_FILE_SIZE_30) {
      messageError = 'Please ensure your file size is less than 30MB';
    }

    try {
      if (!messageError) {
        const { data } = await genPresignUrl({
          fileName: encodeURIComponent(validFiles?.name.toLowerCase())
        });
        const url = data?.url;
        await axios.put(url, validFiles, {
          headers: {
            'content-disposition': 'inline',
            'Content-Type': getContentTypeAvatar(validFiles?.name.toLowerCase()),
            'x-amz-acl': 'public-read'
          }
        });
      }
    } catch (err) {
      messageError = 'An error occurred while uploading the image. Please choose another image!';
    }
    const result = {
      file: validFiles,
      fileName: validFiles?.name || null,
      messageError,
      url: messageError ? '' : URL.createObjectURL(validFiles)
    };
    setFileImg(result);
  };

  const handleFileInputChange = (event) => {
    const newFiles = [...event.target.files];
    validateFiles(newFiles);
    if (isMobile) {
      toast({
        render: () => (
          <ToastBtnMessage
            title={
              "Note: Your uploaded image will be displayed in the 'Profile Review' section below. Please scroll down to view it!"
            }
            isError={false}
            isHiddenButton
            color="jet-black"
          />
        ),
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right'
      });
    }
  };

  const onSubmit = async (formData) => {
    try {
      setIsLoading(true);
      let payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
        description: formData.description,
        socialLinks: {}
      };

      const socialPlatforms = ['facebook', 'instagram', 'linkedin', 'twitter'];
      socialPlatforms.forEach((platform) => {
        if (formData[platform]) {
          payload.socialLinks[platform] = formData[platform];
        }
      });

      if (fileImg?.fileName) {
        payload = {
          ...payload,
          fileName: encodeURIComponent(fileImg.fileName.toLowerCase())
        };
      }

      const res = await updateProfile(payload);
      setProfile({ ...profile, ...res });

      // no status code meaning success
      if (!res.statusCode) {
        // Track profile update event only on success
        DataLayer.trackUpdateProfile();

        toast({
          render: () => (
            <ToastBtnMessage
              title={'Your profile has been updated successfully!'}
              isError={false}
              color="jet-black"
              isHiddenButton
            />
          ),
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'top-right'
        });
      } else {
        throw new Error('Failed to update profile!');
      }
    } catch (err) {
      toast({
        render: () => (
          <ToastBtnMessage title={err.message} isError={true} color="jet-black" isHiddenButton />
        ),
        status: 'error',
        duration: 2000,
        isClosable: true,
        position: 'top-right'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (profile) {
      // Add a small delay to prevent interference with form submission
      const timeoutId = setTimeout(() => {
        methods.reset({
          ...profile,
          username: profile?.username || '',
          facebook: profile?.socialLinks?.facebook,
          instagram: profile?.socialLinks?.instagram,
          linkedin: profile?.socialLinks?.linkedin,
          twitter: profile?.socialLinks?.twitter
        });
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [profile]);

  return (
    <div>
      <IntroComponent title="Public Profile" des="Update current profile details" />
      <div className="bg-white flex flex-col-reverse md:flex-row gap-5 px-0 md:px-[34px] py-5 rounded-[12px] border border-05-seasalt-white mt-6 shadow-[0px_4px_4px_0px_#F2F3F780]">
        <div className="w-full md:w-[50%]">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <div className="px-4 md:pr-[34px] md:pl-0 flex flex-col gap-5 border-r border-[#E6E7EB] pb-6 border-b md:border-b-0">
                <div>
                  <p className="font-[600] text-sm">
                    Your photo <i className="text-[#808080]">(optional)</i>
                  </p>
                  <p className="text-[#808080] text-sm">
                    This will be displayed on your public profile.
                  </p>
                  <div className="w-full cursor-pointer mt-[32px] text-center">
                    <DropzoneComponent
                      title="Click to upload"
                      isError={fileImg?.messageError}
                      desc="File types supported: PNG, JPG, JPEG, GIF. Max size: 30MB"
                      onChange={handleFileInputChange}
                      validateFiles={validateFiles}
                      acceptFileTypes={'image/png, image/jpeg, image/jpg, image/gif'}
                    />
                    {fileImg?.messageError && (
                      <p className="mt-2 text-sm !text-[#DE1414]">{fileImg.messageError}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-6">
                  <div className="w-full">
                    <p className="text-[#808080]">First Name</p>
                    <FormInputCounter
                      p={'0px 0px 8px 0px'}
                      border={'none'}
                      borderColor={'unset'}
                      borderRadius={'none !important'}
                      boxShadow={'none !important'}
                      borderBottom={'1px solid'}
                      borderBottomColor={'mid-gray'}
                      backgroundColor={'white'}
                      name="firstName"
                      maxLength={30}
                      component={InputHookForm}
                      bottomContentProps={{
                        mt: '4px',
                        display: 'flex'
                      }}
                    />
                  </div>
                  <div className="w-full">
                    <p className="text-[#808080]">Last Name</p>
                    <FormInputCounter
                      p={'0px 0px 8px 0px'}
                      border={'none'}
                      borderColor={'unset'}
                      borderRadius={'none !important'}
                      boxShadow={'none !important'}
                      borderBottom={'1px solid'}
                      borderBottomColor={'mid-gray'}
                      backgroundColor={'white'}
                      name="lastName"
                      maxLength={30}
                      component={InputHookForm}
                      bottomContentProps={{
                        mt: '4px',
                        display: 'flex'
                      }}
                    />
                  </div>
                </div>
                <div className="w-full">
                  <p className="text-[#808080]">Username</p>
                  <FormInputCounter
                    p={'0px 0px 8px 0px'}
                    border={'none'}
                    borderColor={'unset'}
                    borderRadius={'none !important'}
                    boxShadow={'none !important'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mid-gray'}
                    backgroundColor={'white'}
                    name="username"
                    minLength={3}
                    maxLength={20}
                    component={InputHookForm}
                    bottomContentProps={{
                      mt: '4px',
                      display: 'flex'
                    }}
                  />
                </div>
                <div className="w-full">
                  <p className="text-[#808080]">About me</p>
                  <FormInputCounter
                    p={'0px 0px 8px 0px'}
                    bgColor={'white'}
                    border={'none'}
                    borderColor={'unset'}
                    borderRadius={'none !important'}
                    boxShadow={'none !important'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mid-gray'}
                    _focusVisible={{
                      borderColor: 'twygs-green'
                    }}
                    placeholder=""
                    name="description"
                    css={{
                      '&::-webkit-scrollbar': {
                        width: '8px'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#e6e7eb',
                        borderRadius: '30px'
                      }
                    }}
                    component={TextAreaHookForm}
                    maxLength={1000}
                    bottomContentProps={{
                      mt: '4px',
                      display: 'flex'
                    }}
                  />
                </div>
                <div className="w-full">
                  <div className="flex gap-[10px]">
                    <img src={FacebookIcon} alt="" />
                    <p className="text-[#808080]">Facebook</p>
                  </div>
                  <InputHookForm
                    p={'0px 0px 8px 0px'}
                    border={'none'}
                    borderColor={'unset'}
                    borderRadius={'none !important'}
                    boxShadow={'none !important'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mid-gray'}
                    backgroundColor={'white'}
                    name="facebook"
                  />
                </div>
                <div className="w-full">
                  <div className="flex gap-[10px]">
                    <img src={InstagramIcon} alt="" />
                    <p className="text-[#808080]">Instagram</p>
                  </div>
                  <InputHookForm
                    p={'0px 0px 8px 0px'}
                    border={'none'}
                    borderColor={'unset'}
                    borderRadius={'none !important'}
                    boxShadow={'none !important'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mid-gray'}
                    backgroundColor={'white'}
                    name="instagram"
                  />
                </div>
                <div className="w-full">
                  <div className="flex gap-[10px]">
                    <img src={LinkedinIcon} alt="" />
                    <p className="text-[#808080]">Linkedin</p>
                  </div>
                  <InputHookForm
                    p={'0px 0px 8px 0px'}
                    border={'none'}
                    borderColor={'unset'}
                    borderRadius={'none !important'}
                    boxShadow={'none !important'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mid-gray'}
                    backgroundColor={'white'}
                    name="linkedin"
                  />
                </div>
                <div className="w-full">
                  <div className="flex gap-[10px]">
                    <img src={TwitterIcon} alt="" />
                    <p className="text-[#808080]">Twitter/X</p>
                  </div>
                  <InputHookForm
                    p={'0px 0px 8px 0px'}
                    border={'none'}
                    borderColor={'unset'}
                    borderRadius={'none !important'}
                    boxShadow={'none !important'}
                    borderBottom={'1px solid'}
                    borderBottomColor={'mid-gray'}
                    backgroundColor={'white'}
                    name="twitter"
                  />
                </div>
                <div className="flex flex-col md:flex-row justify-end gap-3">
                  <Button
                    variant={'outline'}
                    className="px-[18px] h-[42px]"
                    onClick={() => {
                      methods.reset();
                      setFileImg({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={fileImg?.messageError || isLoading}
                    className="px-[18px] h-[42px] order-first md:order-last"
                  >
                    Update profile
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
        <div className="w-full md:w-[50%]">
          <PreviewProfile avataUpload={fileImg.url} />
        </div>
      </div>
    </div>
  );
};

export default ProfileComponent;
